import { useCallback, useEffect, useMemo } from 'react';
import { useDispatch } from 'react-redux';
import { useRouter } from 'next/router';
import isEmpty from 'lodash/isEmpty';
import { fetchSolutionFilters, fetchSolutions } from '@actions/quizTaking';
import {
  getCurrentQuizType,
  getCurrentSessionUID,
  getHasDefaultSection,
  getQuizSolutions,
  getSolutionFirstLevelFilters
} from '@selectors/quizTaking';
import { useSelectorWithShallowEqual } from '@hooks/reduxHooks';
import { DEFAULT_SOLUTION_TYPE } from '@cont/Quiz/constants';

const getNtaResultSolutionsKey = (sessionUID) =>
  `${sessionUID}_${DEFAULT_SOLUTION_TYPE.value}__`;

const useNtaSolutionQuestions = ({ enabled = true } = {}) => {
  const dispatch = useDispatch();
  const { query } = useRouter();
  const { quizUID } = query;

  const sessionUID = useSelectorWithShallowEqual(getCurrentSessionUID);
  const quizType = useSelectorWithShallowEqual(getCurrentQuizType);
  const hasDefaultSection = useSelectorWithShallowEqual(
    getHasDefaultSection(quizUID)
  );
  const solutionFirstLevelFilters = useSelectorWithShallowEqual(
    getSolutionFirstLevelFilters(sessionUID)
  );

  const key = sessionUID ? getNtaResultSolutionsKey(sessionUID) : null;
  const solutions = useSelectorWithShallowEqual(
    key ? getQuizSolutions(key) : () => null
  );

  const solutionFetchParams = useMemo(
    () => ({
      hasDefaultSection,
      solutionType: DEFAULT_SOLUTION_TYPE.value,
      sectionUID: '',
      topicUID: ''
    }),
    [hasDefaultSection]
  );

  useEffect(() => {
    if (
      !enabled ||
      !quizUID ||
      !sessionUID ||
      !isEmpty(solutionFirstLevelFilters)
    ) {
      return;
    }

    dispatch(fetchSolutionFilters(quizUID, sessionUID));
  }, [dispatch, enabled, quizUID, sessionUID, solutionFirstLevelFilters]);

  useEffect(() => {
    if (!enabled || !quizUID || !sessionUID || !key) {
      return;
    }

    if (solutions?.results?.length || solutions?.isFetching) {
      return;
    }

    dispatch(
      fetchSolutions(
        quizUID,
        sessionUID,
        quizType,
        key,
        solutionFetchParams,
        true,
        true
      )
    );
  }, [
    enabled,
    key,
    quizUID,
    quizType,
    sessionUID,
    solutionFetchParams,
    dispatch,
    solutions?.isFetching,
    solutions?.results?.length
  ]);

  const loadMoreSolutions = useCallback(() => {
    if (
      !enabled ||
      !quizUID ||
      !sessionUID ||
      !key ||
      !solutions?.next ||
      solutions.isFetching
    ) {
      return Promise.resolve();
    }

    return dispatch(
      fetchSolutions(
        quizUID,
        sessionUID,
        quizType,
        key,
        solutionFetchParams,
        true,
        false
      )
    );
  }, [
    dispatch,
    enabled,
    key,
    quizType,
    quizUID,
    sessionUID,
    solutionFetchParams,
    solutions?.isFetching,
    solutions?.next
  ]);

  const solutionByUid = useMemo(() => {
    const byUid = {};

    (solutions?.results ?? []).forEach((question) => {
      byUid[question.uid] = question;
    });

    return byUid;
  }, [solutions?.results]);

  return {
    solutionByUid,
    loadMoreSolutions,
    hasMoreSolutions: !!solutions?.next,
    isFetchingSolutions: !!solutions?.isFetching
  };
};

export default useNtaSolutionQuestions;
